import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import {
  AttachMoney,
  Add,
  LocalDining,
  LocalHospital,
  Build,
  Category
} from '@mui/icons-material';
import { useData } from '../contexts/DataContext';
import { formatCurrency } from '../utils/currency';

const ExpenditureManagement = ({ selectedBatch }) => {
  const { expenditures, batches, addExpenditure } = useData();
  const [open, setOpen] = useState(false);
  const [newExpenditure, setNewExpenditure] = useState({
    batch_id: '',
    category: '',
    type: '',
    quantity: '1',
    unit_price: '',
    amount: '',
    description: ''
  });

  // Pre-populate batch when selectedBatch is provided
  useEffect(() => {
    if (selectedBatch && selectedBatch.id) {
      setNewExpenditure(prev => ({
        ...prev,
        batch_id: selectedBatch.id
      }));
    }
  }, [selectedBatch]);

  const handleSubmit = async () => {
    try {
      if (newExpenditure.batch_id === "shared") {
        // Handle shared expenditure - distribute across all active batches
        const activeBatches = batches.filter(batch => batch.status === 'Active');
        
        if (activeBatches.length === 0) {
          alert('No active batches found to distribute the expenditure');
          return;
        }
        
        // Calculate per-batch amount based on bird count
        const totalBirds = activeBatches.reduce((sum, batch) => sum + (batch.current_count || batch.count || 0), 0);
        const totalAmount = parseFloat(newExpenditure.amount);
        
        // Create expenditure for each batch proportionally
        for (const batch of activeBatches) {
          const birdCount = batch.current_count || batch.count || 0;
          const proportionalAmount = totalBirds > 0 ? (birdCount / totalBirds) * totalAmount : totalAmount / activeBatches.length;
          
          const expenditureData = {
            batch_id: batch.id,
            expenditures: [{
              category: newExpenditure.category,
              type: newExpenditure.type,
              description: `${newExpenditure.description} (Shared: ${birdCount}/${totalBirds} birds)`,
              quantity: parseFloat(newExpenditure.quantity),
              unit_price: parseFloat(newExpenditure.unit_price),
              amount: proportionalAmount
            }]
          };
          
          await addExpenditure(expenditureData);
        }
      } else {
        // Handle regular expenditure for specific batch
        const expenditureData = {
          batch_id: newExpenditure.batch_id,
          expenditures: [{
            category: newExpenditure.category,
            type: newExpenditure.type,
            description: newExpenditure.description,
            quantity: parseFloat(newExpenditure.quantity),
            unit_price: parseFloat(newExpenditure.unit_price),
            amount: parseFloat(newExpenditure.amount)
          }]
        };
        
        await addExpenditure(expenditureData);
      }
      
      setOpen(false);
      setNewExpenditure({
        batch_id: "",
        category: "",
        type: "",
        quantity: "1",
        unit_price: "",
        amount: "",
        description: ""
      });
    } catch (error) {
      console.error("Failed to add expenditure:", error);
      alert('Failed to add expenditure. Please try again.');
    }
  };

  const handleAmountChange = (value) => {
    const quantity = parseFloat(newExpenditure.quantity) || 1;
    const unitPrice = parseFloat(value) || 0;
    const totalAmount = quantity * unitPrice;
    
    setNewExpenditure(prev => ({
      ...prev,
      unit_price: value,
      amount: totalAmount.toString()
    }));
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'food': return <LocalDining color="primary" />;
      case 'medicine': return <LocalHospital color="secondary" />;
      case 'equipment': return <Build color="info" />;
      default: return <Category color="action" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'food': return 'primary';
      case 'medicine': return 'secondary';
      case 'equipment': return 'info';
      default: return 'default';
    }
  };

  const totalExpenditures = expenditures.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Expenditure Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpen(true)}
        >
          Add Expenditure
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Total Expenditures: {formatCurrency(totalExpenditures)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {expenditures.length} expenditure records
          </Typography>
        </CardContent>
      </Card>

      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell align="right">Total Amount</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenditures.map((expenditure) => (
              <TableRow key={expenditure.id}>
                <TableCell>
                  {expenditure.expenditure_date ? 
                    new Date(expenditure.expenditure_date).toLocaleDateString() : 
                    new Date(expenditure.created_at).toLocaleDateString()
                  }
                </TableCell>
                <TableCell>
                  <Chip 
                    label={expenditure.batch_code || `Batch ${expenditure.batch_id}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getCategoryIcon(expenditure.category)}
                    <Chip 
                      label={expenditure.category}
                      size="small"
                      color={getCategoryColor(expenditure.category)}
                    />
                  </Box>
                </TableCell>
                <TableCell>{expenditure.type}</TableCell>
                <TableCell>{expenditure.quantity} {expenditure.unit || 'units'}</TableCell>
                <TableCell align="right">
                  {formatCurrency(expenditure.unit_price)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(expenditure.amount)}
                </TableCell>
                <TableCell>{expenditure.description || '-'}</TableCell>
              </TableRow>
            ))}
            {expenditures.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">No expenditures recorded yet</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Expenditure Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Expenditure</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Batch</InputLabel>
              <Select
                value={newExpenditure.batch_id}
                onChange={(e) => setNewExpenditure(prev => ({ ...prev, batch_id: e.target.value }))}
              >
                <MenuItem value="shared">Others/Shared (Distribute across all batches)</MenuItem>
                {batches.map((batch) => (
                  <MenuItem key={batch.id} value={batch.id}>
                    {batch.batch_code || batch.code} - {batch.type} ({batch.current_count} birds)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newExpenditure.category}
                onChange={(e) => setNewExpenditure(prev => ({ ...prev, category: e.target.value }))}
              >
                <MenuItem value="Food">Food</MenuItem>
                <MenuItem value="Medicine">Medicine</MenuItem>
                <MenuItem value="Equipment">Equipment</MenuItem>
                <MenuItem value="Utilities">Utilities</MenuItem>
                <MenuItem value="Labor">Labor</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Type/Description"
              value={newExpenditure.type}
              onChange={(e) => setNewExpenditure(prev => ({ ...prev, type: e.target.value }))}
              placeholder="e.g., Starter Feed, Vaccination, etc."
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Quantity"
                type="number"
                value={newExpenditure.quantity}
                onChange={(e) => setNewExpenditure(prev => ({ ...prev, quantity: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Unit Price (TZS)"
                type="number"
                value={newExpenditure.unit_price}
                onChange={(e) => handleAmountChange(e.target.value)}
                fullWidth
              />
              <TextField
                label="Total Amount (TZS)"
                type="number"
                value={newExpenditure.amount}
                onChange={(e) => setNewExpenditure(prev => ({ ...prev, amount: e.target.value }))}
                fullWidth
              />
            </Box>

            <TextField
              label="Additional Description"
              value={newExpenditure.description}
              onChange={(e) => setNewExpenditure(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!newExpenditure.batch_id || !newExpenditure.category || !newExpenditure.type || !newExpenditure.amount}
          >
            Add Expenditure
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenditureManagement;
